/**
 * The ONE live-game store (PlayChess V2).
 *
 * It is mutated in exactly two ways:
 *   1. `applyMessage(type, data)` — one socket message, one pure reduce, ONE
 *      `set()`. (PlayChess fires ~12 setStates per turn message.)
 *   2. the `actions` (packet builders) below, which read `getState()`
 *      synchronously and write at most one `set()` each.
 *
 * It lives outside React on purpose: the drawer's `RenderOnFocus` unmounts the
 * screen on blur (navigators/Application.tsx), and a `start_game_response`
 * that lands while the screen is away must still be applied.
 */
import {create} from 'zustand';

import {createActions, LiveGameActions} from './liveGame/actions';
import {reduce} from './liveGame/reducers';
import {
  LiveGameState,
  SessionSlice,
  initialArena,
  initialAssisted,
  initialChallenge,
  initialGame,
  initialLobby,
  initialMeta,
  initialSession,
  initialState,
  initialTournament,
  initialWatch,
} from './liveGame/state';

export type {LiveGameState} from './liveGame/state';
export {
  initialState,
  initialSession,
  initialLobby,
  initialGame,
  initialAssisted,
  initialChallenge,
  initialTournament,
  initialArena,
  initialWatch,
  initialMeta,
};

/**
 * The socket is owned by SocketProvider; the screen injects a getter for it
 * (see PlayChessV2/hooks/useGameplayHandlers) so the actions never hold a
 * stale instance and the store never imports React context.
 */
let socketProvider: () => any = () => null;

export const setSocketProvider = (getSocket: () => any): void => {
  socketProvider = getSocket;
};

export type LiveGameStore = LiveGameState & {
  /** the ONLY inbound mutation path */
  applyMessage: (type: string, data: any) => void;
  setSession: (patch: Partial<SessionSlice>) => void;
  /** full reset (new tab / logout); a reload re-hydrates via fetch_game_state */
  reset: () => void;
  actions: LiveGameActions;
};

export const useLiveGame = create<LiveGameStore>((set, get) => ({
  ...initialState,

  applyMessage: (type, data) => {
    set(state => reduce(state, type, data));
    // The ONE non-pure step of the inbound path: some requests are PROMISES
    // (`actions.joinTournament`) and the server answers them by echoing the
    // packet's requestId. A pure reducer cannot resolve a promise, so the
    // actions settle theirs here, after the state has already moved.
    get().actions.settleInbound(type, data);
  },

  setSession: patch => {
    set(state => ({...state, session: {...state.session, ...patch}}));
  },

  reset: () => {
    set(state => ({...state, ...initialState}));
  },

  actions: createActions({
    getState: () => get(),
    setState: updater => set(state => updater(state)),
    getSocket: () => socketProvider(),
  }),
}));

/** For `getState()` / `subscribe()` outside React (effects, tests, actions). */
export const liveGameStore = useLiveGame;

export default useLiveGame;
